import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import useSiteSettings from '@/hooks/useSiteSettings';
import { ArrowRight, TrendingUp, Building2, Banknote, Rocket, ChevronRight, Users, ShieldCheck, Calendar, Tag } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FONT_HEADING = "'Cormorant Garamond', Cambria, Georgia, serif";
const FONT_BODY = "'Jost', sans-serif";

/* ─── 1. MARQUEE STRIP ─── */
function MarqueeStrip({ s }) {
  const items = (s?.marquee_items || 'Senior Secured,Deal-Level Access,Invitation Only,Curated Deals,Private Network,Verified Investors Only').split(',').map(i => i.trim());
  const doubled = [...items, ...items];
  return (
    <div className="bg-[#A0743A] overflow-hidden py-2.5" data-testid="marquee-strip">
      <div className="marquee-track">
        {doubled.map((t, i) => (
          <span key={i} className="text-[#FAF8F5] text-[11px] uppercase tracking-[0.2em] font-medium whitespace-nowrap mx-8" style={{ fontFamily: FONT_BODY }}>
            {t} <span className="text-[#FAF8F5]/40 mx-4">&bull;</span>
          </span>
        ))}
      </div>
    </div>
  );
}

/* ─── HERO SECTION with staggered fade-up ─── */
function HeroSection({ s }) {
  const handleRequestAccess = () => {
    document.getElementById('request-access')?.scrollIntoView({ behavior: 'smooth' });
  };
  const bgImage = s?.hero_bg_image || "https://images.unsplash.com/photo-1761437856297-e7d0191f9a42?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBnb2xkJTIwdGV4dHVyZXxlbnwwfHx8fDE3NzYwODM5Njh8MA&ixlib=rb-4.1.0&q=85";
  return (
    <section data-testid="hero-section" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('${bgImage}')` }} />
      <div className="absolute inset-0 bg-[#1A1A1A]/75" />
      <div className="relative z-10 max-w-4xl mx-auto text-center px-6">
        {/* 8. Each element fades up with 0.15s stagger */}
        <p className="text-[#C9A96E] text-sm uppercase tracking-[0.3em] mb-6 opacity-0 animate-fade-in-up stagger-1" style={{ fontFamily: FONT_BODY }}>{s?.hero_eyebrow || "India's Private Deal Network"}</p>
        <h1 className="text-5xl md:text-6xl lg:text-7xl text-[#FAF8F5] mb-2 tracking-tight leading-[1.1] opacity-0 animate-fade-in-up stagger-2" style={{ fontFamily: FONT_HEADING }}>{s?.hero_headline_1 || 'Curated Access to'}</h1>
        <h1 className="text-5xl md:text-6xl lg:text-7xl text-[#A0743A] mb-6 tracking-tight leading-[1.1] opacity-0 animate-fade-in-up stagger-3" style={{ fontFamily: FONT_HEADING }}>{s?.hero_headline_2 || 'Private Capital Markets'}</h1>
        <div className="w-16 h-[1px] bg-[#C9A96E] mx-auto mb-6 opacity-0 animate-fade-in-up stagger-4" />
        <p className="text-[#999] text-lg md:text-xl max-w-2xl mx-auto mb-12 leading-relaxed opacity-0 animate-fade-in-up stagger-5" style={{ fontFamily: FONT_BODY }}>{s?.hero_subtitle || 'A gated, invitation-only deal discovery platform connecting verified HNIs, Family Offices, and Institutional Investors with curated private market opportunities.'}</p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center opacity-0 animate-fade-in-up stagger-6">
          <button data-testid="request-access-cta" onClick={handleRequestAccess} className="bg-[#A0743A] text-[#FAF8F5] px-10 py-4 text-sm uppercase tracking-[0.15em] font-semibold hover:bg-[#C9A96E] transition-colors flex items-center justify-center gap-3" style={{ fontFamily: FONT_BODY }}>
            {s?.hero_cta_primary || 'Request Access'} <ArrowRight size={16} />
          </button>
          <a href="#how-it-works" className="border border-[#A0743A] text-[#A0743A] px-10 py-4 text-sm uppercase tracking-[0.15em] font-semibold hover:bg-[#A0743A]/10 transition-colors" style={{ fontFamily: FONT_BODY }} data-testid="learn-more-btn">{s?.hero_cta_secondary || 'Learn More'}</a>
        </div>
      </div>
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce"><ChevronRight size={20} className="text-[#C9A96E] rotate-90" /></div>
    </section>
  );
}

/* ─── 10. STATS SECTION with animated counters ─── */
function AnimatedCounter({ target, suffix = '', prefix = '' }) {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started) { setStarted(true); }
    }, { threshold: 0.5 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    const num = parseFloat(target.replace(/[^0-9.]/g, ''));
    if (isNaN(num)) { setCount(target); return; }
    const duration = 1500;
    const steps = 60;
    const increment = num / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= num) { current = num; clearInterval(timer); }
      setCount(Number.isInteger(num) ? Math.round(current) : current.toFixed(1));
    }, duration / steps);
    return () => clearInterval(timer);
  }, [started, target]);

  return (
    <span ref={ref}>
      {started ? `${prefix}${count}${suffix}` : `${prefix}0${suffix}`}
    </span>
  );
}

function StatsSection({ s }) {
  return (
    <section data-testid="stats-section" className="py-20 px-6 md:px-12 bg-[#1A1A1A] border-y border-[#A0743A]/15">
      <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8">
        <div className="text-center">
          <p className="text-3xl md:text-4xl text-[#A0743A] mb-2" style={{ fontFamily: FONT_HEADING }}><AnimatedCounter target={s?.stat_1_value||"10"} prefix={s?.stat_1_prefix||"$"} suffix={s?.stat_1_suffix||"B+"} /></p>
          <p className="text-[#999] text-xs uppercase tracking-[0.15em]" style={{ fontFamily: FONT_BODY }}>{s?.stat_1_label||'Annual Deal Volume'}</p>
        </div>
        <div className="text-center">
          <p className="text-3xl md:text-4xl text-[#A0743A] mb-2" style={{ fontFamily: FONT_HEADING }}><AnimatedCounter target={s?.stat_2_value||"90"} prefix={s?.stat_2_prefix||"80-"} suffix={s?.stat_2_suffix||"%"} /></p>
          <p className="text-[#999] text-xs uppercase tracking-[0.15em]" style={{ fontFamily: FONT_BODY }}>{s?.stat_2_label||'HNI Share of AIF Inflows'}</p>
        </div>
        <div className="text-center">
          <p className="text-3xl md:text-4xl text-[#A0743A] mb-2" style={{ fontFamily: FONT_HEADING }}><AnimatedCounter target={s?.stat_3_value||"15"} prefix={s?.stat_3_prefix||"$"} suffix={s?.stat_3_suffix||"L Cr"} /></p>
          <p className="text-[#999] text-xs uppercase tracking-[0.15em]" style={{ fontFamily: FONT_BODY }}>{s?.stat_3_label||'Private Credit Market'}</p>
        </div>
        <div className="text-center">
          <p className="text-3xl md:text-4xl text-[#A0743A] mb-2" style={{ fontFamily: FONT_HEADING }}><AnimatedCounter target={s?.stat_4_value||"0"} suffix={s?.stat_4_suffix||""} /></p>
          <p className="text-[#999] text-xs uppercase tracking-[0.15em]" style={{ fontFamily: FONT_BODY }}>{s?.stat_4_label||'Public Listings'}</p>
        </div>
      </div>
    </section>
  );
}

/* ─── 14. ASSET CLASS SECTION with min ticket ─── */
function AssetClassSection({ s }) {
  const icons = [TrendingUp, Rocket, Banknote, Building2];
  const classes = [1, 2, 3, 4].map((i, idx) => ({
    icon: icons[idx],
    title: s?.[`asset_${i}_title`] || ['Private Equity', 'Venture Capital', 'Private Debt', 'Venture Debt'][idx],
    desc: s?.[`asset_${i}_desc`] || '',
    minTicket: s?.[`asset_${i}_ticket`] || '',
  }));
  const disclaimer = s?.asset_return_disclaimer || 'Target returns only. Not guaranteed. Subject to deal terms.';
  return (
    <section id="asset-classes" data-testid="asset-classes-section" className="py-28 px-6 md:px-12 bg-[#1A1A1A]">
      <div className="max-w-7xl mx-auto">
        <p className="text-[#C9A96E] text-sm uppercase tracking-[0.2em] font-semibold mb-4" style={{ fontFamily: FONT_BODY }}>{s?.asset_section_eyebrow || 'What We Offer'}</p>
        <h2 className="text-4xl md:text-5xl text-[#FAF8F5] mb-16 tracking-tight" style={{ fontFamily: FONT_HEADING }}>{s?.asset_section_headline_1 || 'Four Asset Classes.'}<br />{s?.asset_section_headline_2 || 'One Private Network.'}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
          {classes.map((c, i) => (
            <div key={c.title} className={`p-10 border border-[#A0743A]/15 hover:border-[#A0743A]/40 transition-all group ${i < 2 ? 'border-b-0 md:border-b' : ''} ${i % 2 === 0 ? 'md:border-r-0' : ''}`} data-testid={`asset-class-${c.title.toLowerCase().replace(/\s+/g, '-')}`}>
              <c.icon size={32} className="text-[#A0743A] mb-6 group-hover:text-[#C9A96E] transition-colors" strokeWidth={1.25} />
              <h3 className="text-2xl text-[#FAF8F5] mb-4" style={{ fontFamily: FONT_HEADING }}>{c.title}</h3>
              <p className="text-[#999] text-sm leading-relaxed mb-3" style={{ fontFamily: FONT_BODY }}>{c.desc}</p>
              <p className="text-[#555] text-xs" style={{ fontFamily: FONT_BODY }}>{c.minTicket}</p>
              {/* 2. Return disclaimer */}
              <p className="text-[#555] text-[10px] mt-2 italic" style={{ fontFamily: FONT_BODY }}>{disclaimer}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── HOW IT WORKS ─── */
function HowItWorksSection({ s }) {
  const steps = [1, 2, 3, 4].map(i => ({
    num: String(i).padStart(2, '0'),
    title: s?.[`step_${i}_title`] || ['Request Access', 'Verification', 'Discover Deals', 'Request IM & Invest'][i - 1],
    desc: s?.[`step_${i}_desc`] || '',
  }));
  const bgImage = s?.how_bg_image || "https://images.unsplash.com/photo-1760172287483-02d382f63a6f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBnb2xkJTIwdGV4dHVyZXxlbnwwfHx8fDE3NzYwODM5Njh8MA&ixlib=rb-4.1.0&q=85";
  return (
    <section id="how-it-works" data-testid="how-it-works-section" className="py-28 px-6 md:px-12 relative" style={{ backgroundImage: `url('${bgImage}')`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
      <div className="absolute inset-0 bg-[#1A1A1A]/90" />
      <div className="relative z-10 max-w-7xl mx-auto">
        <p className="text-[#C9A96E] text-sm uppercase tracking-[0.2em] font-semibold mb-4" style={{ fontFamily: FONT_BODY }}>{s?.how_section_eyebrow || 'The Process'}</p>
        <h2 className="text-4xl md:text-5xl text-[#FAF8F5] mb-16 tracking-tight" style={{ fontFamily: FONT_HEADING }}>{s?.how_section_headline || 'How It Works'}</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-0">
          {steps.map((st, i) => (
            <div key={st.num} className={`p-8 ${i < 3 ? 'md:border-r border-[#A0743A]/20' : ''}`} data-testid={`step-${st.num}`}>
              <span className="text-[#A0743A] text-5xl font-light block mb-4" style={{ fontFamily: FONT_HEADING }}>{st.num}</span>
              <h3 className="text-lg text-[#FAF8F5] mb-3" style={{ fontFamily: FONT_HEADING }}>{st.title}</h3>
              <p className="text-[#999] text-sm leading-relaxed" style={{ fontFamily: FONT_BODY }}>{st.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── WHO WE SERVE ─── */
function WhoWeServeSection({ s }) {
  const icons = [Users, Building2, TrendingUp, ShieldCheck];
  const segments = [1, 2, 3, 4].map((i, idx) => ({
    icon: icons[idx],
    title: s?.[`who_${i}_title`] || ['HNIs & UHNIs', 'Family Offices', 'PE & VC Funds', 'Institutional Investors'][idx],
    criteria: s?.[`who_${i}_criteria`] || '',
    desc: s?.[`who_${i}_desc`] || '',
  }));
  return (
    <section id="who-we-serve" data-testid="who-we-serve-section" className="py-28 px-6 md:px-12 bg-[#1A1A1A]">
      <div className="max-w-7xl mx-auto">
        <p className="text-[#C9A96E] text-sm uppercase tracking-[0.2em] font-semibold mb-4" style={{ fontFamily: FONT_BODY }}>{s?.who_section_eyebrow || 'Our Investors'}</p>
        <h2 className="text-4xl md:text-5xl text-[#FAF8F5] mb-16 tracking-tight" style={{ fontFamily: FONT_HEADING }}>{s?.who_section_headline || 'Who We Serve'}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {segments.map(seg => (
            <div key={seg.title} className="bg-[#FAF8F5] border-l-4 border-[#A0743A] p-8 group hover:-translate-y-1 transition-transform" data-testid={`segment-${seg.title.toLowerCase().replace(/[&\s]+/g, '-')}`}>
              <seg.icon size={28} className="text-[#A0743A] mb-4" strokeWidth={1.25} />
              <h3 className="text-xl text-[#1A1A1A] mb-1" style={{ fontFamily: FONT_HEADING }}>{seg.title}</h3>
              <p className="text-[#A0743A] text-xs uppercase tracking-wider font-semibold mb-3" style={{ fontFamily: FONT_BODY }}>{seg.criteria}</p>
              <p className="text-[#555] text-sm leading-relaxed" style={{ fontFamily: FONT_BODY }}>{seg.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── 11. PHILOSOPHY / BRAND STORY with decorative quote ─── */
function PhilosophySection({ s }) {
  const bgImage = s?.philosophy_bg_image || "https://images.unsplash.com/photo-1770819372133-1ca304fc0687?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBkYXJrfGVufDB8fHx8MTc3NjA4Mzk2Mnww&ixlib=rb-4.1.0&q=85";
  return (
    <section id="brand-story" data-testid="brand-story-section" className="py-28 px-6 md:px-12 relative" style={{ backgroundImage: `url('${bgImage}')`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
      <div className="absolute inset-0 bg-[#1A1A1A]/85" />
      <div className="relative z-10 max-w-4xl mx-auto">
        {/* Quote block (without decorative quote mark) */}
        {s?.philosophy_quote && s.philosophy_quote.trim() && (
          <div className="relative mb-16">
            <blockquote>
              <p className="text-[32px] md:text-[52px] text-[#FAF8F5] leading-[1.2] italic tracking-tight" style={{ fontFamily: FONT_HEADING }} data-testid="philosophy-quote">
                {s.philosophy_quote}
              </p>
            </blockquote>
          </div>
        )}

        <div className="text-center">
          <p className="text-[#C9A96E] text-sm uppercase tracking-[0.2em] font-semibold mb-4" style={{ fontFamily: FONT_BODY }}>{s?.philosophy_eyebrow || 'Our Story'}</p>
          <h2 className="text-4xl md:text-5xl text-[#FAF8F5] mb-8 tracking-tight" style={{ fontFamily: FONT_HEADING }}>{s?.brand_story_headline || 'Built by Deal Originators, for Capital Allocators'}</h2>
          <div className="w-16 h-[1px] bg-[#C9A96E] mx-auto mb-8" />
          <p className="text-[#FAF8F5]/80 text-lg leading-relaxed mb-6" style={{ fontFamily: FONT_BODY }}>
            {s?.brand_story_text_1 || "Sàwai Capital Dealroom was born from a simple observation: India's private credit market exceeds $10 billion in annual deal volume, growing at 18-20% annually. Yet HNIs and Family Offices — who represent 80-90% of AIF inflows — lack a structured, digital channel for curated deal discovery."}
          </p>
          <p className="text-[#FAF8F5]/80 text-lg leading-relaxed" style={{ fontFamily: FONT_BODY }}>
            {s?.brand_story_text_2 || "We built what didn't exist. A private, multi-asset-class deal network where every opportunity is committee-reviewed, every investor is verified, and every interaction is designed with the elegance that serious capital deserves."}
          </p>
        </div>
      </div>
    </section>
  );
}

/* ─── 4 & 5. REQUEST ACCESS with validation, success, WhatsApp CTA ─── */
function RequestAccessSection({ s }) {
  const [form, setForm] = useState({ name: '', email: '', entity_name: '', entity_type: '', aum_range: '', asset_class_interest: [], message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = 'Name is required';
    if (!form.email.trim()) errs.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(form.email)) errs.email = 'Invalid email';
    if (!form.entity_type) errs.entity_type = 'Select entity type';
    if (!form.aum_range) errs.aum_range = 'Select AUM range';
    if (form.asset_class_interest.length === 0) errs.asset_class_interest = 'Select at least one';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await fetch(`${API}/access-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      setSubmitted(true);
    } catch {
      alert('Submission failed. Please try again.');
    }
    setLoading(false);
  };

  const toggleAssetClass = (cls) => {
    setForm(prev => ({
      ...prev,
      asset_class_interest: prev.asset_class_interest.includes(cls)
        ? prev.asset_class_interest.filter(c => c !== cls)
        : [...prev.asset_class_interest, cls]
    }));
  };

  if (submitted) {
    return (
      <section id="request-access" data-testid="request-access-section" className="py-28 px-6 md:px-12 bg-[#1A1A1A]">
        <div className="max-w-2xl mx-auto text-center">
          <ShieldCheck size={48} className="text-[#A0743A] mx-auto mb-6" strokeWidth={1.25} />
          <h2 className="text-3xl text-[#A0743A] mb-4" style={{ fontFamily: FONT_HEADING }}>{s?.access_success_headline || 'Thank you.'}</h2>
          <p className="text-[#A0743A] text-lg mb-8" style={{ fontFamily: FONT_BODY }}>{s?.access_success_message || 'Our team will contact you within 48 hours.'}</p>
          {/* 5. WhatsApp CTA */}
          <p className="text-[#999] text-sm mb-4" style={{ fontFamily: FONT_BODY }}>{s?.access_whatsapp_prompt || 'Prefer to speak directly?'}</p>
          <a
            href={`https://wa.me/${s?.whatsapp_number || '919871871888'}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="whatsapp-cta"
            className="inline-flex items-center gap-2 bg-[#25D366] text-[#FAF8F5] px-8 py-3 text-sm font-semibold uppercase tracking-wider hover:bg-[#20BD5C] transition-colors"
            style={{ fontFamily: FONT_BODY }}
          >
            WhatsApp Us <ArrowRight size={14} />
          </a>
        </div>
      </section>
    );
  }

  const fieldError = (key) => errors[key] ? <p className="text-red-400 text-xs mt-1">{errors[key]}</p> : null;

  return (
    <section id="request-access" data-testid="request-access-section" className="py-28 px-6 md:px-12 bg-[#1A1A1A]">
      <div className="max-w-2xl mx-auto">
        <p className="text-[#C9A96E] text-sm uppercase tracking-[0.2em] font-semibold mb-4" style={{ fontFamily: FONT_BODY }}>{s?.access_section_eyebrow || 'Join the Network'}</p>
        <h2 className="text-4xl text-[#FAF8F5] mb-4 tracking-tight" style={{ fontFamily: FONT_HEADING }}>{s?.access_section_headline || 'Request Access'}</h2>
        <p className="text-[#999] mb-12" style={{ fontFamily: FONT_BODY }}>{s?.access_section_subtitle || 'Platform access is restricted to verified investors. Submit your details and our team will review your application.'}</p>

        <form onSubmit={handleSubmit} className="space-y-8" noValidate>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <label className="text-sm tracking-wide text-[#A0743A] mb-2 block" style={{ fontFamily: FONT_BODY }}>Full Name *</label>
              <input data-testid="access-name-input" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="bg-transparent border-b border-[#A0743A]/40 text-[#FAF8F5] placeholder-[#555] py-3 focus:border-[#C9A96E] focus:outline-none w-full" style={{ fontFamily: FONT_BODY }} placeholder="Your full name" />
              {fieldError('name')}
            </div>
            <div>
              <label className="text-sm tracking-wide text-[#A0743A] mb-2 block" style={{ fontFamily: FONT_BODY }}>Email *</label>
              <input data-testid="access-email-input" type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} className="bg-transparent border-b border-[#A0743A]/40 text-[#FAF8F5] placeholder-[#555] py-3 focus:border-[#C9A96E] focus:outline-none w-full" style={{ fontFamily: FONT_BODY }} placeholder="you@example.com" />
              {fieldError('email')}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <label className="text-sm tracking-wide text-[#A0743A] mb-2 block" style={{ fontFamily: FONT_BODY }}>Entity Name</label>
              <input data-testid="access-entity-input" value={form.entity_name} onChange={e => setForm(p => ({ ...p, entity_name: e.target.value }))} className="bg-transparent border-b border-[#A0743A]/40 text-[#FAF8F5] placeholder-[#555] py-3 focus:border-[#C9A96E] focus:outline-none w-full" style={{ fontFamily: FONT_BODY }} placeholder="Family office / Company" />
            </div>
            <div>
              <label className="text-sm tracking-wide text-[#A0743A] mb-2 block" style={{ fontFamily: FONT_BODY }}>Entity Type *</label>
              <select data-testid="access-entity-type-select" value={form.entity_type} onChange={e => setForm(p => ({ ...p, entity_type: e.target.value }))} className="bg-[#1A1A1A] border-b border-[#A0743A]/40 text-[#FAF8F5] py-3 focus:border-[#C9A96E] focus:outline-none w-full" style={{ fontFamily: FONT_BODY }}>
                <option value="">Select type</option>
                <option value="HNI">HNI</option>
                <option value="UHNI">UHNI</option>
                <option value="Family Office">Family Office</option>
                <option value="PE Fund">PE Fund</option>
                <option value="VC Fund">VC Fund</option>
                <option value="NBFC">NBFC</option>
                <option value="Other">Other</option>
              </select>
              {fieldError('entity_type')}
            </div>
          </div>
          <div>
            <label className="text-sm tracking-wide text-[#A0743A] mb-2 block" style={{ fontFamily: FONT_BODY }}>AUM / Net Worth Range *</label>
            <select data-testid="access-aum-select" value={form.aum_range} onChange={e => setForm(p => ({ ...p, aum_range: e.target.value }))} className="bg-[#1A1A1A] border-b border-[#A0743A]/40 text-[#FAF8F5] py-3 focus:border-[#C9A96E] focus:outline-none w-full" style={{ fontFamily: FONT_BODY }}>
              <option value="">Select range</option>
              <option value="5-25 Cr">INR 5-25 Crore</option>
              <option value="25-100 Cr">INR 25-100 Crore</option>
              <option value="100-500 Cr">INR 100-500 Crore</option>
              <option value="500+ Cr">INR 500+ Crore</option>
            </select>
            {fieldError('aum_range')}
          </div>
          <div>
            <label className="text-sm tracking-wide text-[#A0743A] mb-4 block" style={{ fontFamily: FONT_BODY }}>Asset Class Interest *</label>
            <div className="flex flex-wrap gap-3">
              {['Private Equity', 'Venture Capital', 'Private Debt', 'Venture Debt'].map(cls => (
                <button key={cls} type="button" data-testid={`access-asset-${cls.toLowerCase().replace(/\s+/g, '-')}`} onClick={() => toggleAssetClass(cls)} className={`px-5 py-2.5 text-xs uppercase tracking-wider transition-colors ${form.asset_class_interest.includes(cls) ? 'bg-[#A0743A] text-[#FAF8F5]' : 'border border-[#A0743A]/40 text-[#A0743A] hover:bg-[#A0743A]/10'}`} style={{ fontFamily: FONT_BODY }}>{cls}</button>
              ))}
            </div>
            {fieldError('asset_class_interest')}
          </div>
          <div>
            <label className="text-sm tracking-wide text-[#A0743A] mb-2 block" style={{ fontFamily: FONT_BODY }}>Message (Optional)</label>
            <textarea data-testid="access-message-input" value={form.message} onChange={e => setForm(p => ({ ...p, message: e.target.value }))} rows={3} className="bg-transparent border-b border-[#A0743A]/40 text-[#FAF8F5] placeholder-[#555] py-3 focus:border-[#C9A96E] focus:outline-none w-full resize-none" style={{ fontFamily: FONT_BODY }} placeholder="Tell us about your investment interests..." />
          </div>

          <button data-testid="access-submit-btn" type="submit" disabled={loading} className="bg-[#A0743A] text-[#FAF8F5] px-10 py-4 text-sm uppercase tracking-[0.15em] font-semibold hover:bg-[#C9A96E] transition-colors disabled:opacity-50 flex items-center gap-3" style={{ fontFamily: FONT_BODY }}>
            {loading ? 'Submitting...' : 'Submit Access Request'} <ArrowRight size={16} />
          </button>

          {/* 5. WhatsApp CTA below submit */}
          <div className="pt-4">
            <p className="text-[#999] text-sm mb-3" style={{ fontFamily: FONT_BODY }}>{s?.access_whatsapp_prompt || 'Prefer to speak directly?'}</p>
            <a
              href={`https://wa.me/${s?.whatsapp_number || '919871871888'}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="whatsapp-cta"
              className="inline-flex items-center gap-2 bg-[#25D366] text-[#FAF8F5] px-6 py-3 text-xs font-semibold uppercase tracking-wider hover:bg-[#20BD5C] transition-colors"
              style={{ fontFamily: FONT_BODY }}
            >
              WhatsApp Us <ArrowRight size={14} />
            </a>
          </div>
        </form>
      </div>
    </section>
  );
}


/* ─── BLOG / NEWS SECTION ─── */
function BlogNewsSection({ s }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const res = await fetch(`${API}/blog/posts`);
        if (res.ok) setPosts(await res.json());
      } catch {}
      setLoading(false);
    };
    fetchPosts();
  }, []);

  if (loading || posts.length === 0) return null;

  const featured = posts[0];
  const rest = posts.slice(1, 4);
  const viewAllLabel = s?.blog_view_all_label || 'View All Articles';

  return (
    <section id="insights" data-testid="blog-news-section" className="py-28 px-6 md:px-12 bg-[#1A1A1A]">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-end justify-between mb-16">
          <div>
            <p className="text-[#C9A96E] text-sm uppercase tracking-[0.2em] font-semibold mb-4" style={{ fontFamily: FONT_BODY }}>{s?.blog_section_eyebrow || 'Insights & Analysis'}</p>
            <h2 className="text-4xl md:text-5xl text-[#FAF8F5] tracking-tight" style={{ fontFamily: FONT_HEADING }}>{s?.blog_section_headline || 'Latest from the Dealroom'}</h2>
          </div>
          <Link to="/blog" className="hidden md:flex items-center gap-2 text-[#A0743A] text-xs uppercase tracking-wider font-semibold hover:text-[#C9A96E] transition-colors" style={{ fontFamily: FONT_BODY }} data-testid="view-all-blogs">
            {viewAllLabel} <ArrowRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Featured Post */}
          <Link to={`/blog/${featured.slug}`} className="group" data-testid={`featured-post-${featured.slug}`}>
            <div className="border border-[#A0743A]/15 hover:border-[#A0743A]/40 transition-all h-full">
              {featured.cover_image && (
                <div className="overflow-hidden">
                  <img src={featured.cover_image} alt={featured.title} className="w-full h-auto group-hover:scale-105 transition-transform duration-500 opacity-80 group-hover:opacity-100" />
                </div>
              )}
              <div className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  {featured.tags?.slice(0, 2).map(tag => (
                    <span key={tag} className="text-[#A0743A] text-[10px] uppercase tracking-wider font-semibold flex items-center gap-1" style={{ fontFamily: FONT_BODY }}>
                      <Tag size={10} /> {tag}
                    </span>
                  ))}
                </div>
                <h3 className="text-2xl md:text-3xl text-[#FAF8F5] mb-4 group-hover:text-[#A0743A] transition-colors tracking-tight leading-snug" style={{ fontFamily: FONT_HEADING }}>
                  {featured.title}
                </h3>
                {featured.excerpt && (
                  <p className="text-[#999] text-sm leading-relaxed mb-6 line-clamp-3" style={{ fontFamily: FONT_BODY }}>{featured.excerpt}</p>
                )}
                <div className="flex items-center gap-3 text-[#555] text-xs" style={{ fontFamily: FONT_BODY }}>
                  <Calendar size={12} />
                  {featured.published_at ? new Date(featured.published_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : ''}
                </div>
              </div>
            </div>
          </Link>

          {/* Secondary Posts */}
          <div className="flex flex-col gap-6">
            {rest.map(post => (
              <Link to={`/blog/${post.slug}`} key={post.post_id} className="group border border-[#A0743A]/15 hover:border-[#A0743A]/40 transition-all p-6 flex gap-6" data-testid={`blog-card-${post.slug}`}>
                {post.cover_image && (
                  <div className="w-28 h-28 shrink-0 overflow-hidden hidden sm:block">
                    <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover opacity-70 group-hover:opacity-100 transition-opacity" />
                  </div>
                )}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    {post.tags?.slice(0, 1).map(tag => (
                      <span key={tag} className="text-[#A0743A] text-[9px] uppercase tracking-wider font-semibold" style={{ fontFamily: FONT_BODY }}>#{tag}</span>
                    ))}
                    <span className="text-[#555] text-[10px]" style={{ fontFamily: FONT_BODY }}>
                      {post.published_at ? new Date(post.published_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : ''}
                    </span>
                  </div>
                  <h4 className="text-lg text-[#FAF8F5] group-hover:text-[#A0743A] transition-colors mb-2 tracking-tight leading-snug" style={{ fontFamily: FONT_HEADING }}>
                    {post.title}
                  </h4>
                  {post.excerpt && (
                    <p className="text-[#999] text-xs leading-relaxed line-clamp-2" style={{ fontFamily: FONT_BODY }}>{post.excerpt}</p>
                  )}
                </div>
              </Link>
            ))}

            {posts.length === 1 && (
              <div className="border border-[#A0743A]/10 border-dashed p-8 flex items-center justify-center">
                <p className="text-[#555] text-sm text-center" style={{ fontFamily: FONT_BODY }}>More articles coming soon. <br />Stay tuned for market insights.</p>
              </div>
            )}
          </div>
        </div>

        {/* Mobile "View All" */}
        <div className="mt-10 text-center md:hidden">
          <Link to="/blog" className="inline-flex items-center gap-2 text-[#A0743A] text-xs uppercase tracking-wider font-semibold hover:text-[#C9A96E] transition-colors" style={{ fontFamily: FONT_BODY }}>
            {viewAllLabel} <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </section>
  );
}


export default function LandingPage() {
  const s = useSiteSettings();

  return (
    <div className="bg-[#1A1A1A] min-h-screen">
      <Navbar />
      <HeroSection s={s} />
      <MarqueeStrip s={s} />
      <StatsSection s={s} />
      <AssetClassSection s={s} />
      <HowItWorksSection s={s} />
      <WhoWeServeSection s={s} />
      <PhilosophySection s={s} />
      <BlogNewsSection s={s} />
      <RequestAccessSection s={s} />
      <Footer s={s} />
    </div>
  );
}
